/**
 * QuestionTextRules — the short/long-text configuration card.
 *
 * Shape follows the operator's 2026-07-30 review:
 *  · Placeholder + prefilled value are plain fields (they're not validation).
 *  · Required + Hidden are NOT here — they apply to every field type, so they
 *    live in QuestionCommonRules. Read-only stays, being text-specific.
 *  · Everything that IS validation (pattern, custom regex, length, the error
 *    message) sits behind ONE toggle, because the flat form was too complex.
 *  · No per-language rows (multilingual comes later) and no "test an answer" —
 *    the live preview is now interactive, so it IS the tester.
 *  · No per-language rows (multilingual comes later) and no "test an answer" —
 *    the live preview is now interactive, so it IS the tester.
 */
import { type CSSProperties } from "react";
import { alpha, Card, Dropdown, Icon, T, Toggle } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";
import {
  Q_TEXT_VALIDATIONS,
  qTextValidation,
  compileRule,
  type QTextRules,
  type QTextValidationId,
} from "./text-validation";

const inp = (extra?: CSSProperties): CSSProperties => ({
  width: "100%", height: 38, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8,
  background: T.white, color: T.n1, fontSize: 13.5, fontFamily: T.font, boxSizing: "border-box", ...extra,
});
const lbl: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n2, marginBottom: 5 };
const hint: CSSProperties = { fontSize: 11.5, color: T.soft, lineHeight: 1.45, marginTop: 5, textWrap: "pretty" };
const mono: CSSProperties = { fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)" };

function Row({ on, onToggle, title, sub }: { on: boolean; onToggle: () => void; title: string; sub: string }) {
  return (
    <label onClick={onToggle} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "2px 0" }}>
      <span style={{ marginTop: 1, flexShrink: 0 }}><Toggle on={on} /></span>
      <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span style={{ fontSize: 13.5, color: T.n1, fontWeight: 500 }}>{title}</span>
        <span style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45, textWrap: "pretty" }}>{sub}</span>
      </span>
    </label>
  );
}

function Note({ tone, children }: { tone: "info" | "warn"; children: React.ReactNode }) {
  const warn = tone === "warn";
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: 10, background: warn ? alpha(T.orange1, 10) : T.p4, border: `1px solid ${warn ? alpha(T.orange1, 28) : T.p3}`, borderRadius: 8, fontSize: 12, color: T.n1, lineHeight: 1.5 }}>
      <span style={{ color: warn ? T.orange1 : T.p1, flexShrink: 0, marginTop: 1, display: "inline-flex" }}>
        {warn ? <Icon.warn size={13} /> : <Icon.info size={13} />}
      </span>
      <span style={{ textWrap: "pretty" }}>{children}</span>
    </div>
  );
}

export function QuestionTextRules({
  type,
  hidden,
  rules,
  placeholder,
  defaultValue,
  onChange,
  onPlaceholder,
  onDefaultValue,
}: {
  type: string;
  /** Question-level hidden flag — read-only is meaningless while it's on. */
  hidden: boolean;
  rules: QTextRules;
  placeholder: string;
  defaultValue: string;
  onChange: (next: QTextRules) => void;
  onPlaceholder: (next: string) => void;
  onDefaultValue: (next: string) => void;
}) {
  const t = useT();
  const set = (patch: Partial<QTextRules>) => onChange({ ...rules, ...patch });
  const preset = qTextValidation(rules.validation);
  const { error: patternError } = compileRule(rules);
  const on = rules.validationEnabled === true;

  const min = rules.minLength ?? null;
  const max = rules.maxLength ?? null;
  const lengthConflict = typeof min === "number" && typeof max === "number" && min > 0 && max > 0 && min > max;

  const num = (raw: string): number | null => {
    const n = parseInt(raw, 10);
    return raw.trim() === "" || Number.isNaN(n) ? null : Math.max(0, n);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 6 }}>
      <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("questions.textRules.field", "Field")}</div>

        <div>
          <div style={lbl}>{t("questions.textRules.placeholder", "Placeholder")}</div>
          <input
            value={placeholder}
            onChange={(e) => onPlaceholder(e.target.value)}
            placeholder={t("questions.textRules.placeholderPlaceholder", "Greyed hint shown while the field is empty")}
            data-testid="qtext-placeholder"
            style={inp()}
          />
          <div style={hint}>{t("questions.textRules.placeholderHint", "Not an answer — it disappears as soon as the customer types.")}</div>
        </div>

        <div>
          <div style={lbl}>{t("questions.textRules.defaultValue", "Prefilled value")}</div>
          <input
            value={defaultValue}
            onChange={(e) => onDefaultValue(e.target.value)}
            placeholder={t("questions.textRules.defaultValuePlaceholder", "The field starts with this text")}
            data-testid="qtext-default-value"
            style={inp()}
          />
          <div style={hint}>{t("questions.textRules.defaultValueHint", "A real answer the customer can edit — unless the field is read-only.")}</div>
        </div>

        <div style={{ height: 1, background: T.border }} />

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Row
            on={!!rules.readOnly}
            onToggle={() => set({ readOnly: !rules.readOnly })}
            title={t("questions.textRules.readOnly", "Read-only")}
            sub={t("questions.textRules.readOnlySub", "Visible but not editable — for values prefilled from the CRM or a URL parameter.")}
          />
        </div>

        {hidden && rules.readOnly ? (
          <Note tone="info">{t("questions.textRules.hiddenReadOnlyNote", "Read-only has no effect while the field is hidden — nobody sees it either way.")}</Note>
        ) : null}
      </Card>

      <Card style={{ padding: 16, display: "flex", flexDirection: "column", gap: on ? 14 : 0 }}>
        <Row
          on={on}
          onToggle={() => set({ validationEnabled: !on, validation: on ? "none" : (rules.validation ?? "none") })}
          title={t("questions.textRules.validateAnswer", "Validate the answer")}
          sub={t("questions.textRules.validateAnswerSub", "Restrict what counts as a valid answer — a format, a length, or your own pattern.")}
        />

        {on ? (
          <>
            <div>
              <div style={lbl}>{t("questions.textRules.validation", "Format")}</div>
              <Dropdown
                options={Q_TEXT_VALIDATIONS.map((v) => ({ value: v.id, label: t(v.labelKey, v.label) }))}
                value={preset.id}
                onChange={(v) => set({ validation: v as QTextValidationId })}
                aria-label={t("questions.textRules.validation", "Format")}
                testid="qtext-validation"
                style={{ gap: 0, width: "100%" }}
              />
              {preset.id !== "none" && preset.id !== "custom" ? (
                <div style={hint}>
                  <span style={mono}>{preset.pattern}</span>
                  {preset.example ? ` · ${t("questions.textRules.egExample", "e.g.")} ${preset.example}` : ""}
                </div>
              ) : null}
              {preset.formatOnly ? (
                <div style={{ ...hint, marginTop: 6 }}>
                  {t("questions.textRules.formatOnlyShort", "Checks the format only — the checksum in this number is not verified by a pattern.")}
                </div>
              ) : null}
            </div>

            {preset.id === "custom" ? (
              <div>
                <div style={lbl}>{t("questions.textRules.customPattern", "Custom regex")}</div>
                <input
                  value={rules.customPattern ?? ""}
                  onChange={(e) => set({ customPattern: e.target.value })}
                  placeholder="^[A-Z]{2}-\d{4}$"
                  spellCheck={false}
                  data-testid="qtext-custom-pattern"
                  style={inp({ ...mono, borderColor: patternError ? T.red1 : T.n4 })}
                />
                {patternError ? (
                  <div style={{ ...hint, color: T.red1 }}>{`${t("questions.textRules.invalidRegex", "Not a valid regular expression")} — ${patternError}`}</div>
                ) : (
                  <div style={hint}>{t("questions.textRules.customPatternHint", "Anchor with ^ and $ to match the whole answer. Written without delimiters or flags; matching is Unicode-aware.")}</div>
                )}
              </div>
            ) : null}

            {preset.singleLine && type === "long" ? (
              <Note tone="warn">
                {t("questions.textRules.longSingleLineWarn", "This pattern cannot match text containing line breaks, so it will reject any multi-line answer. Use a custom pattern if long answers should span lines.")}
              </Note>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <div style={lbl}>{t("questions.textRules.minLength", "Min length")}</div>
                <input
                  type="number" min={0} value={min ?? ""}
                  onChange={(e) => set({ minLength: num(e.target.value) })}
                  placeholder={t("questions.textRules.noMinimum", "No minimum")}
                  data-testid="qtext-min-length"
                  style={inp({ borderColor: lengthConflict ? T.red1 : T.n4 })}
                />
                <div style={hint}>{t("questions.textRules.minHint", "Characters. Leave empty for no minimum.")}</div>
              </div>
              <div>
                <div style={lbl}>{t("questions.textRules.maxLength", "Max length")}</div>
                <input
                  type="number" min={0} value={max ?? ""}
                  onChange={(e) => set({ maxLength: num(e.target.value) })}
                  placeholder={t("questions.textRules.noMaximum", "No maximum")}
                  data-testid="qtext-max-length"
                  style={inp({ borderColor: lengthConflict ? T.red1 : T.n4 })}
                />
                <div style={hint}>{t("questions.textRules.maxHint", "Characters. Also caps typing in the field.")}</div>
              </div>
            </div>
            {lengthConflict ? (
              <Note tone="warn">{t("questions.textRules.lengthConflict", "Min length is greater than max length — no answer can satisfy both.")}</Note>
            ) : null}

            <div>
              <div style={lbl}>{t("questions.textRules.errorMessage", "Error message")}</div>
              <input
                value={rules.errorMessages?.[""] ?? ""}
                onChange={(e) => set({ errorMessages: { ...(rules.errorMessages ?? {}), "": e.target.value } })}
                placeholder={t("questions.textRules.errorMessagePlaceholder", "Shown when the answer doesn't match — e.g. “Enter an AHV number like 756.1234.5678.97”")}
                data-testid="qtext-error-default"
                style={inp()}
              />
              <div style={hint}>{t("questions.textRules.errorMessageHintSimple", "Left empty, the platform's own message is shown. Try it in the live preview.")}</div>
            </div>
          </>
        ) : null}
      </Card>
    </div>
  );
}
